import { API_CONFIG } from '../config/api';
import { RegisterResponse, GetUsersResponse } from '../types';
import { getStoredUTMs } from '../utils/utm';
import { getFunnelLocale, getFunnelSource } from '../utils/loops';

/**
 * API service stub.
 * Provides functions for user registration, profile updates, member fetching,
 * and company user management.
 */

/**
 * Helper to get the current auth token from localStorage.
 */
function getAuthToken(): string | null {
    return localStorage.getItem('accessToken');
}

/**
 * Helper to build authorized headers.
 */
function authHeaders(): Record<string, string> {
    const token = getAuthToken();
    const headers: Record<string, string> = {
        'Content-Type': 'application/json',
    };
    if (token) {
        headers['Authorization'] = `Bearer ${token}`;
    }
    return headers;
}

/**
 * Register a new user.
 *
 * @param email - User email address
 * @param name - User name (can be empty string)
 * @param password - Optional password
 * @param referralCode - Optional referral code
 * @returns RegisterResponse with user data, access token, and refresh token
 */
export async function registerUser(
    email: string,
    name: string,
    password?: string,
    referralCode?: string
): Promise<RegisterResponse> {
    const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.REGISTER}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, name, password, referralCode }),
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Registration failed with status ${response.status}`);
    }

    return response.json();
}

export type ReferralValidateResult = {
    isValid: boolean;
    referrerFirstName?: string;
    welcomePoints?: number;
    discountAmount?: number;
};

/**
 * Public GET /referral/validate — used on landing with ?ref= to show the invite banner.
 * Invalid/expired codes return isValid:false; network failures also resolve as invalid (no UI error).
 */
export async function validateReferralCode(referralCode: string): Promise<ReferralValidateResult> {
    const code = referralCode.trim();
    if (!code) {
        return { isValid: false };
    }
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 8000);
    try {
        const params = new URLSearchParams({ ref: code });
        const response = await fetch(
            `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.VALIDATE_REFERRAL}?${params.toString()}`,
            {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' },
                signal: controller.signal,
            },
        );
        const payload = await response.json().catch(() => null);
        const data = payload?.data;
        if (!response.ok || !data) {
            return { isValid: false };
        }
        const firstName =
            typeof data.referrerFirstName === 'string' ? data.referrerFirstName.trim() : '';
        return {
            isValid: data.isValid === true,
            referrerFirstName: firstName || undefined,
            welcomePoints: typeof data.welcomePoints === 'number' ? data.welcomePoints : undefined,
            discountAmount: typeof data.discountAmount === 'number' ? data.discountAmount : undefined,
        };
    } catch {
        return { isValid: false };
    } finally {
        clearTimeout(timeoutId);
    }
}

/**
 * Update the current user's profile.
 *
 * @param profileData - An object containing any profile fields to update
 *   (e.g. firstName, password, originWeight, weightGoal, height, heightUnit, weightUnit)
 * @returns A response object indicating success/failure
 */
export async function updateProfile(
    profileData: Record<string, any>
): Promise<{ success: boolean; message?: string; data?: any }> {
    const response = await fetch(`${API_CONFIG.BASE_URL}/users/profile`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify(profileData),
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Profile update failed with status ${response.status}`);
    }

    return response.json();
}

/**
 * Fetch a paginated list of members (admin endpoint).
 *
 * @param params - Query parameters for pagination, search, and status filter
 * @returns GetUsersResponse containing users array and pagination info
 */
export async function fetchMembers(params: {
    page?: number;
    limit?: number;
    search?: string;
    status?: string;
}): Promise<GetUsersResponse> {
    const queryParams = new URLSearchParams();

    if (params.page) queryParams.set('page', String(params.page));
    if (params.limit) queryParams.set('limit', String(params.limit));
    if (params.search) queryParams.set('search', params.search);
    if (params.status) queryParams.set('status', params.status);

    const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.GET_USERS}?${queryParams.toString()}`;

    const response = await fetch(url, {
        method: 'GET',
        headers: authHeaders(),
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to fetch members with status ${response.status}`);
    }

    return response.json();
}

/**
 * Add a new company user by email (admin endpoint).
 *
 * @param email - The email address of the user to add
 * @returns A response object indicating success/failure
 */
/**
 * Fire-and-forget lead capture via the /api/loops-lead serverless function.
 * Sends contact properties + event in a single Loops events/send call.
 */
export function sendLoopsLead(
    email: string,
    firstName: string,
    quizData: {
        primaryGoal?: string | null;
        mainBarrier?: string | null;
        rewardPreference?: string | null;
        ageGroup?: string | null;
        activityLevel?: string | null;
        walkingHistory?: string | null;
        sleepQuality?: string | null;
        physicalBarriers?: string[] | null;
        motivation?: string | null;
        commitment?: string | null;
        weightToLose?: number | null;
        weight?: number | null;
        weightGoal?: number | null;
        weightUnit?: string | null;
        targetDate?: string | null;
    }
): void {
    const utms = getStoredUTMs();

    const body: Record<string, string> = {
        email,
        funnelSource: getFunnelSource(),
        funnelLocale: getFunnelLocale(),
    };
    if (firstName) body.firstName = firstName;
    if (quizData.primaryGoal) body.primaryGoal = quizData.primaryGoal;
    if (quizData.mainBarrier) body.mainBarrier = quizData.mainBarrier;
    if (quizData.rewardPreference) body.rewardPreference = quizData.rewardPreference;
    if (quizData.ageGroup) body.ageGroup = quizData.ageGroup;
    if (quizData.activityLevel) body.activityLevel = quizData.activityLevel;
    if (quizData.walkingHistory) body.walkingHistory = quizData.walkingHistory;
    if (quizData.sleepQuality) body.sleepQuality = quizData.sleepQuality;
    if (quizData.physicalBarriers?.length) body.physicalBarriers = JSON.stringify(quizData.physicalBarriers);
    if (quizData.motivation) body.motivation = quizData.motivation;
    if (quizData.commitment) body.commitment = quizData.commitment;
    if (quizData.weightToLose) body.weightToLose = String(quizData.weightToLose);
    if (quizData.weight) body.weight = String(quizData.weight);
    if (quizData.weightGoal) body.weightGoal = String(quizData.weightGoal);
    if (quizData.weightUnit)   body.weightUnit   = quizData.weightUnit === 'lbs' ? 'lb' : quizData.weightUnit;
    if (quizData.targetDate)   body.targetDate   = quizData.targetDate;
    if (utms.utm_source) body.utm_source = utms.utm_source;
    if (utms.utm_medium) body.utm_medium = utms.utm_medium;
    if (utms.utm_campaign) body.utm_campaign = utms.utm_campaign;
    if (utms.utm_content) body.utm_content = utms.utm_content;
    if (utms.utm_term) body.utm_term = utms.utm_term;

    fetch('/api/loops-lead', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
    })
        .then((res) => {
            if (!res.ok) res.text().then((t) =>
                console.error('sendLoopsLead error:', res.status, t)
            );
        })
        .catch((err) => console.error('sendLoopsLead network error:', err));
}

export async function addCompanyUser(
    email: string
): Promise<{ success: boolean; message?: string; data?: any }> {
    const response = await fetch(`${API_CONFIG.BASE_URL}/admin/users`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ email }),
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to add company user with status ${response.status}`);
    }

    return response.json();
}
